import { useEffect, useState } from "react";
import {
  DEFAULT_BATCH_SIZE,
  type ReportTableRequest,
  type ReportTableResponse,
} from "../workers/report-table-protocol";

export interface ReportTableState {
  columns: string[];
  rows: string[][];
  total: number;
  isLoading: boolean;
  isError: boolean;
  isComplete: boolean;
}

const EMPTY: ReportTableState = {
  columns: [],
  rows: [],
  total: 0,
  isLoading: false,
  isError: false,
  isComplete: false,
};

/**
 * Owns the report-table Web Worker's lifecycle for one (url, enabled) pair:
 * spawns a worker, asks it to parse, accumulates the `columns`/`rows`/`done`/
 * `error` messages it streams back into state, and tears it down on cleanup.
 *
 * WHY setState ON EVERY BATCH, NOT THROTTLED:
 * `DEFAULT_BATCH_SIZE` (2000) already makes batches coarse — a 15k-row file
 * is ~8 messages, a 100k-row file is ~50 — and the worker yields a macrotask
 * between each one, so these arrive spread across the parse's wall-clock
 * time, not in a tight synchronous burst. Each update here does one
 * `Array.prototype.concat`, which copies row-array *references* (not cell
 * strings), so even the last, largest batch of a big file costs well under a
 * millisecond. Buffering batches in a ref and flushing on a timer/rAF would
 * trade that away for no measurable benefit: it delays the exact
 * progressive-render feedback this hook exists to produce, while the state
 * update itself was never the expensive part.
 *
 * The cost worth worrying about lives one level up, in the consumer that
 * turns `rows: string[][]` into `Record<string, unknown>[]` for TanStack
 * Table: naively re-deriving that from the FULL accumulated `rows` array on
 * every batch turns an O(n) job into an O(n · batchCount) one. See the
 * incremental-append comment in tabular-report.tsx, where that is actually
 * fixed.
 */
export const useReportTable = (
  url: string,
  enabled: boolean
): ReportTableState => {
  const [state, setState] = useState<ReportTableState>(EMPTY);

  useEffect(() => {
    if (!enabled || !url) {
      setState(EMPTY);
      return;
    }

    // Guards `onmessage` against messages from a worker this effect has
    // already torn down. `worker.terminate()` stops execution but is not
    // guaranteed to un-queue a message the worker already posted before
    // terminate() ran (browsers may still deliver an in-flight postMessage
    // task) — without this flag such a straggler could land after `url` has
    // already changed and get mixed into the NEW file's state.
    let torndown = false;

    setState({ ...EMPTY, isLoading: true });

    const worker = new Worker(
      new URL("../workers/report-table.worker.ts", import.meta.url),
      { type: "module" }
    );

    worker.onmessage = (event: MessageEvent<ReportTableResponse>) => {
      if (torndown) return;
      const message = event.data;
      if (message.type === "columns") {
        setState(prev => ({ ...prev, columns: message.columns }));
      } else if (message.type === "rows") {
        // Batches arrive in order; append, don't replace.
        setState(prev => ({
          ...prev,
          rows: prev.rows.concat(message.rows),
          total: message.parsedRowCount,
        }));
      } else if (message.type === "done") {
        setState(prev => ({
          ...prev,
          total: message.totalRows,
          isLoading: false,
          isComplete: true,
        }));
      } else if (message.type === "error") {
        setState(prev => ({ ...prev, isLoading: false, isError: true }));
      }
    };

    // A worker that throws during module init/execution (as opposed to
    // posting a { type: "error" } message itself) fires `onerror` instead of
    // `onmessage`; without this the hook would spin on isLoading forever.
    worker.onerror = () => {
      if (torndown) return;
      setState(prev => ({ ...prev, isLoading: false, isError: true }));
    };

    const request: ReportTableRequest = {
      type: "parse",
      url,
      batchSize: DEFAULT_BATCH_SIZE,
    };
    worker.postMessage(request);

    return () => {
      torndown = true;
      const cancel: ReportTableRequest = { type: "cancel" };
      // Best-effort cooperative stop (see report-table.worker.ts's
      // cancellation-coverage note) PLUS an unconditional terminate() — the
      // only hard guarantee, and what actually frees the worker thread.
      worker.postMessage(cancel);
      worker.terminate();
    };
  }, [url, enabled]);

  return state;
};
